import type { CSSProperties } from "react";
import { PW, pewterPanel } from "./pewter-shell";
import { BEVEL_INSET_WHITE, BEVEL_WHITE_FACE } from "@majourneys/shared/ui";

/**
 * The shared pieces of a pewter dialog — the cancel and modify modals, and the
 * booking-details sheet that now opens them.
 *
 * They live here rather than in each modal because the three are seen one on
 * top of another: open "Cancel booking" from the sheet and both are on screen
 * at once, so a shade of grey that drifts between them is not a detail. Every
 * one of these is a plain object, applied via `style`, because the package is
 * also served by the city sites, which do not load the web app's stylesheet.
 */

/** The scrim. Dialogs stack, so a second one darkens the first. */
export const pewterScrim = "bg-[rgba(4,0,51,0.45)] backdrop-blur-[2px]";

/** The dialog body: the pewter frame at the house 4px band. */
export function pewterDialog(radius = 26): CSSProperties {
  return pewterPanel(radius, { gap: 4 }) as CSSProperties;
}

/** A control at rest, and the secondary button: the soft-bevel white face. */
export const pewterControlSoft: CSSProperties = {
  background: BEVEL_WHITE_FACE,
  boxShadow: `${BEVEL_INSET_WHITE}, 0 0 0 1px ${PW.controlEdge}, 0 6px 14px rgba(17,14,57,0.07)`,
};

/** The primary button: the navy gradient with its lift. */
export const pewterPrimaryButton: CSSProperties = {
  background: PW.navyGradient,
  color: "#fff",
  boxShadow: PW.primaryShadow,
};

/**
 * The destructive button. Deep enough to be unmistakable, but a gradient like
 * everything else here rather than the flat `bg-red-600` it replaces — a
 * single flat slab in a pewter dialog reads as a warning sticker someone
 * pasted on afterwards.
 */
export const pewterDangerButton: CSSProperties = {
  background: "linear-gradient(160deg, #C0433F 0%, #A32F2C 55%, #8C2422 100%)",
  color: "#fff",
  boxShadow: "0 8px 18px rgba(140,36,34,0.32), inset 0 1px 0 rgba(255,255,255,0.18)",
};

/**
 * The quiet destructive button — "Cancel booking" where it merely opens the
 * confirm dialog, not where it does the deed. A gradient like its neighbours
 * (owner, 2026-09-10): flat, it was the one sticker left in a row of soft
 * controls. The deep `pewterDangerButton` stays for the button that actually
 * cancels, so the two are never mistaken for each other.
 */
export const pewterDangerSoft: CSSProperties = {
  background: "linear-gradient(180deg, #FCF0EF 0%, #F7E2E0 55%, #F1D5D3 100%)",
  color: PW.red,
  boxShadow:
    "0 0 0 1px rgba(155,44,44,0.18), 0 6px 14px rgba(155,44,44,0.10), inset 0 1px 0 rgba(255,255,255,0.85)",
};

/** An input or textarea: white with one hairline, never the band. */
export const pewterInput: CSSProperties = {
  background: PW.controlFill,
  boxShadow: `0 0 0 1px ${PW.controlEdge}`,
};

/** The small round close button in a dialog's top-right. */
export const pewterCloseButton: CSSProperties = {
  background: PW.controlFill,
  boxShadow: `0 0 0 1px ${PW.controlEdge}`,
};

/** An inline error: the house red on its own tint, no frame. */
export const pewterErrorNote: CSSProperties = {
  background: PW.redTint,
  color: PW.red,
};
